import { useState } from "react";

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
}

interface FilterPanelProps {
  groups: FilterGroup[];
  value: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onReset: () => void;
}

function GroupList({
  groups,
  value,
  onChange,
}: {
  groups: FilterGroup[];
  value: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      {groups.map((group) => (
        <div key={group.key}>
          <p className="mb-2.5 text-xs font-medium tracking-wide text-foreground-500">
            {group.label}
          </p>
          <div className="flex flex-wrap gap-2">
            {group.options.map((option) => {
              const active = value[group.key] === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onChange(group.key, option.value)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FilterPanel({
  groups,
  value,
  onChange,
  onReset,
}: FilterPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      {/* Desktop panel */}
      <aside className="hidden h-fit rounded-lg border border-background-200 bg-background-50 p-5 lg:block">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-sm font-medium text-foreground-950">筛选与排序</h2>
          <button
            type="button"
            onClick={onReset}
            className="whitespace-nowrap text-xs text-accent-600 transition-colors hover:text-accent-700"
          >
            重置
          </button>
        </div>
        <GroupList groups={groups} value={value} onChange={onChange} />
      </aside>

      {/* Mobile trigger */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-800"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-equalizer-line text-base" />
          </span>
          筛选与排序
        </button>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5 animate-fade-up">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-base font-medium text-foreground-950">筛选与排序</h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-foreground-700"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <GroupList groups={groups} value={value} onChange={onChange} />
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={onReset}
                className="flex-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-800"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50"
              >
                完成
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}